import { HttpClient } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { environment } from "../../../environments/environment";
import { UserPayload } from "../models/requests/user-payload.type";
import { catchError, map, Observable, of, tap, throwError } from "rxjs";
import { User } from "../models/dtos/user.type";
import { ServiceRespose } from "../../shared";

@Injectable({
    providedIn: 'root'
})
export class UserService {
    httpClient = inject(HttpClient);

    private readonly baseApiUrl = `${environment.userServiceApiBaseUrl}/api`;
    private readonly USER_KEY = environment.localStorageKeys.currentUser;

    private readonly _currentUser = signal<User | null>(null);

    readonly currentUser = this._currentUser.asReadonly();

    constructor() {
        this.restoreCurrentUser();
    }


    createUser(userPayload: UserPayload): Observable<User>{
        return this.httpClient
            .post<ServiceRespose<User>>(`${this.baseApiUrl}/user`, userPayload)
            .pipe(
                map((res) => res.data),
                catchError(err => throwError(() => err))
            );
    }

    getCurrentUser(): Observable<User> {
        return this.httpClient
            .get<ServiceRespose<User>>(`${this.baseApiUrl}/user`)
            .pipe(
                tap((res) => {
                    this._currentUser.set(res.data);
                    localStorage.setItem(this.USER_KEY, JSON.stringify(res.data));
                }),
                map((res) => res.data),
                catchError(err => throwError(() => err))
            );
    }

    getUserById(userId: string): Observable<User>{
        return this.httpClient
            .get<ServiceRespose<User>>(`${this.baseApiUrl}/user/${userId}`)
            .pipe(
                map((res) => res.data),
                catchError(err => throwError(() => err))
            );
    }

    getAllUsers(): Observable<User[]>{
        return this.httpClient
            .get<ServiceRespose<User[]>>(`${this.baseApiUrl}/user/all`)
            .pipe(
                map((res) => res.data),
                catchError((err)=>of(err))
            );
    }

    updateUser(userId: string, userPayload: UserPayload): Observable<User>{
        return this.httpClient
            .put<ServiceRespose<User>>(`${this.baseApiUrl}/user/${userId}`, userPayload)
            .pipe(
                tap((res) => {
                    this._currentUser.set(res.data);

                    localStorage.removeItem(this.USER_KEY);
                    localStorage.setItem(this.USER_KEY, JSON.stringify(res.data));
                }),
                map((res) => res.data),
                catchError((err)=>of(err))
            );
    }

    clearCurrentUser(): void{
        this._currentUser.set(null);
        localStorage.removeItem(this.USER_KEY);
    }

    restoreCurrentUser(): void{
        const userJson = localStorage.getItem(this.USER_KEY);
        if(userJson){
            try{
                const currUser : User = JSON.parse(userJson);
                this._currentUser.set(currUser);
            } catch(e){
                console.error("Failed to parse user from localStorage:", e);
                this.clearCurrentUser();
            }
        }
    }
}